import {
	CaretRight,
	CheckCircle,
	Files,
	WarningDiamond,
	FlagBannerFold,
} from '@phosphor-icons/react';
import PrimaryButton from '../../../components/atoms/Button';
import AnnualPreviewInfoItem from '../components/AnnualPreviewInfoItem';
import { AnnualPreviewData } from '../../../@types/AnnualPreview.type';
import { useEffect } from 'react';
import { formatCurrency } from '../../../shared/utils/general.util';
import { useNavigate } from 'react-router-dom';
import ROUTES from '../../../shared/constants/routes';
import { AnnualFAQs } from '../components/AnnualFAQs';

const AnnualPreviewStepFinal = ({
	data,
	handleAddToCart,
	isLoadingAddToCart,
	havePrebillingAvailable,
	userHasAnnualSubscription,
}: {
	data: AnnualPreviewData;
	handleAddToCart: () => void;
	isLoadingAddToCart: boolean;
	havePrebillingAvailable: boolean | null;
	userHasAnnualSubscription: boolean;
}) => {
	const navigate = useNavigate();

	const STEPS = [
		{
			label: 'Preparación',
			message:
				'En los primeros 15 días de abril, estaremos trabajando en la prepración de tu declaración anual. Mantente atento a tu correo electrónico en caso de que necesitemos más información.',
		},
		{
			label: 'Presentación en el portal del SAT',
			message:
				'En los últimos 15 días de abril, estataremos presentando tu declaración anual en el portal del SAT. Es importante que sepas que el SAT puede ser algo lento en esta época. Te pedimos paciencia.',
		},
		{
			label: 'Resultado',
			message:
				'Una vez hayamos completado la presentación, te enviaremos el resultado por correo electrónico incluyendo un resumen del mismo y los acuses de presentación y detalle correspondientes.',
		},
	];

	useEffect(() => {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}, []);

	if (userHasAnnualSubscription) {
		return (
			<div className='bg-white rounded-xl p-4 flex flex-col items-center gap-2'>
				{userHasAnnualSubscription && (
					<div className='flex flex-col items-center gap-1'>
						<FlagBannerFold
							size={20}
							weight='duotone'
						/>
						<p className='font-medium'>
							Información importante sobre tu declaración anual 2024
						</p>
					</div>
				)}
				{havePrebillingAvailable && !userHasAnnualSubscription && (
					<div className='flex flex-col items-center gap-2'>
						<CheckCircle
							className='text-h-success-50'
							size={32}
							weight='duotone'
						/>
						<p className='font-medium text-base text-center'>
							Anticipa tu renovación de tu plan de declaración anual 2024.
						</p>
						<p className='text-h-medium text-sm text-center'>
							En el 2024 te suscribiste a nuestro plan de declaración anual. Sin
							embargo, tu renovación aún no se ha generado. Por favor haz clic
							en adelantar mi renovación para empezar a preparar tu declaración
							anual.
						</p>
						<PrimaryButton
							label='Adelantar mi renovación'
							onClick={() => navigate(`${ROUTES.PLANS_AND_PRICES}?flow=annual`)}
							sx='bg-h-success-50 text-white hover:!bg-h-success-50 hover:!opacity-90 my-2'
						/>
					</div>
				)}
				<hr className='w-full my-2' />
				<div>
					{havePrebillingAvailable && !userHasAnnualSubscription && (
						<p className='mb-2 font-bold'>
							Al anticipar empezaremos a trabajar en tu declaración anual
						</p>
					)}
					<div className='flex flex-col gap-2'>
						{STEPS.map((step, index) => (
							<div
								key={`step-${index}`}
								className='flex items-start gap-2'
							>
								<p className='bg-h-primary-94 text-h-primary rounded-full text-sm font-medium border border-h-primary flex justify-center items-center min-w-5 min-h-5 size-5 mt-1'>
									{index + 1}
								</p>
								<div className='flex flex-col'>
									<p className='font-medium'>{step.label}</p>
									<p className='font-light text-sm'>{step.message}</p>
								</div>
							</div>
						))}
					</div>
				</div>

				<hr className='w-full mt-2' />

				<div className='w-full mt-4'>
					<h3 className='font-medium mb-3'>Preguntas frecuentes</h3>
					<div className='flex flex-col gap-2'>
						<AnnualFAQs />
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className='bg-white rounded-xl px-4 py-5 flex flex-col items-center gap-6'>
			<div className='flex flex-col items-center gap-2'>
				<Files
					size={32}
					weight='duotone'
					className='text-h-success-50 '
				/>
				<p className='font-medium text-base'>Tus ingresos de 2024</p>
				<p className='font-medium text-3xl'>
					{formatCurrency(data?.result?.total_incomes)}
				</p>
			</div>
			<div className='w-full flex flex-col gap-3'>
				<AnnualPreviewInfoItem
					label='Ingresos totales'
					value={data.result.total_incomes}
				/>
				<hr />
				<AnnualPreviewInfoItem
					label='Deducciones personales'
					value={data.result.total_deductions}
					classNameValue={
						data.result.total_deductions > 0 ? 'text-h-success-50' : ''
					}
				/>
			</div>
			<PrimaryButton
				onClick={handleAddToCart}
				loading={isLoadingAddToCart}
				label='Adquirir declaración anual'
				sx='bg-h-success-50 text-white hover:!bg-h-success-50 hover:!opacity-90'
				icon={<CaretRight className='min-w-5 min-h-5' />}
			/>

			<div className='bg-h-neutral-95 p-3 rounded-lg flex items-start gap-2 text-h-medium text-xs'>
				<WarningDiamond
					className='min-w-5 min-h-5'
					weight='fill'
				/>
				<div className='flex flex-col gap-2'>
					<p>Nota importante: Este es un cálculo estimado</p>
					<p>
						El resultado final puede variar dependiendo de factores adicionales
						y la revisión detallada de un profesional fiscal.
					</p>
				</div>
			</div>
		</div>
	);
};

export default AnnualPreviewStepFinal;
